import { Auth, CognitoUser } from '@aws-amplify/auth';
import Amplify from '@aws-amplify/core';
import { jwtDecode } from 'jwt-decode';

export interface Identity {
  name: string;
  firstName?: string;
  lastName?: string;
  email: string;
}

// Refresh 5 min before actual expiry so API calls never go out with a token that dies mid-flight.
const TOKEN_EXPIRY_BUFFER_SECONDS = 300;

export const isTokenExpired = (token: string | null | undefined | void): boolean => {
  if (!token) return true;
  try {
    const { exp } = jwtDecode<{ exp: number }>(token);
    const currentTime = Math.floor(Date.now() / 1000);
    return exp - TOKEN_EXPIRY_BUFFER_SECONDS < currentTime;
  } catch (error) {
    console.error('Failed to decode token', error);
    return true;
  }
};

/**
 * Returns a non-expired ID token. When the cached one is stale, Auth.currentSession()
 * uses the Cognito refresh token to mint a new one.
 */
export const getValidUserIdToken = async (): Promise<string | undefined> => {
  const currentToken = localStorage.getItem('userIdToken');
  if (!isTokenExpired(currentToken)) {
    return currentToken as string;
  }

  try {
    const session = await Auth.currentSession();
    const freshToken = session.getIdToken().getJwtToken();
    localStorage.setItem('userIdToken', freshToken);
    return freshToken;
  } catch (e) {
    console.error('Failed to refresh userIdToken — session may be invalid', e);
    return undefined;
  }
};

export const getUser = async (): Promise<Identity | undefined> => {
  try {
    const user: CognitoUser = await Auth.currentAuthenticatedUser();
    if (!user) {
      return;
    }
    const session = user.getSignInUserSession();
    if (!session || !session.isValid()) {
      return;
    }
    const idToken = session.getIdToken();

    localStorage.setItem('userIdToken', idToken.getJwtToken());

    const attributes = idToken.decodePayload();
    return {
      name: `${attributes.given_name} ${attributes.family_name}`,
      firstName: attributes.given_name,
      lastName: attributes.family_name,
      email: attributes.email,
    };
  } catch (e) {
    console.log('getUser failed', e);
  }
  return;
};

export const getIdToken = async (): Promise<string | void> => {
  try {
    const session = await Auth.currentSession();
    return session.getIdToken().getJwtToken();
  } catch (e) {
    return;
  }
};

export const getAccessToken = async (): Promise<string | void> => {
  try {
    const session = await Auth.currentSession();
    return session.getAccessToken().getJwtToken();
  } catch (e) {
    return;
  }
};

export const configureUserAuth = (): void => {
  const redirectUri = window.location.origin;

  Amplify.configure({
    Auth: {
      region: 'us-east-1',
      userPoolId: API_HOST.portalUserPoolId,
      userPoolWebClientId: API_HOST.portalUserPoolWebClientId,
      oauth: {
        domain: API_HOST.portalCognitoDomain,
        scope: ['email', 'openid', 'aws.cognito.signin.user.admin', 'profile'],
        redirectSignIn: redirectUri,
        redirectSignOut: redirectUri,
        responseType: 'code'
      },
      // The Portal session is seeded into localStorage from its cookies (see App.tsx),
      // so Amplify must read from localStorage rather than host-scoped cookies.
      storage: window.localStorage,
    },
  });
};

export const signIn = async (): Promise<void> => {
  try {
    await Auth.federatedSignIn();
  } catch (error) {
    console.error('Error signing in:', error);
    throw error;
  }
};

export const signOut = async (): Promise<void> => {
  try {
    await Auth.signOut();
  } catch (e) {
    throw new Error(e as string | undefined);
  }
};

export const getPortalUrl = (): string => API_HOST.portal_url;
